import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  /** @type {number | undefined} */
  #cartPulseTimeout;

  /** @type {number | undefined} */
  #cartReceiveStartTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    if (this.#cartPulseTimeout) {
      clearTimeout(this.#cartPulseTimeout);
    }
    if (this.#cartReceiveStartTimeout) {
      clearTimeout(this.#cartReceiveStartTimeout);
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    if (this.#cartPulseTimeout) {
      clearTimeout(this.#cartPulseTimeout);
    }
    if (this.#cartReceiveStartTimeout) {
      clearTimeout(this.#cartReceiveStartTimeout);
    }

    cartIcon.classList.remove('cart-icon--receiving');
    this.#cartReceiveStartTimeout = window.setTimeout(() => {
      cartIcon.classList.add('cart-icon--receiving');
      this.#cartPulseTimeout = window.setTimeout(() => {
        cartIcon.classList.remove('cart-icon--receiving');
      }, 320);
    }, 55);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 960);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 * @property {HTMLFieldSetElement | undefined} bundleSelector - The multi-piece bundle selector element.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.#setupBundleSelector();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Optimistically updates the visible in-cart quantity for the current variant.
   * This keeps product-page quantity rules responsive without waiting for another cart fetch.
   * @param {number} quantityToAdd
   * @param {string} [variantId]
   */
  #optimisticallyIncrementCartQuantity(quantityToAdd, variantId) {
    if (!Number.isFinite(quantityToAdd) || quantityToAdd <= 0) return;

    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return;

    if (variantId && variantIdInput.value.toString() !== variantId.toString()) return;

    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      quantitySelector?.querySelector?.('input[data-cart-quantity]')
    );

    const currentCartQty = quantitySelector?.getCurrentValues
      ? quantitySelector.getCurrentValues().cartQuantity
      : parseInt(quantityInput?.getAttribute('data-cart-quantity') || '0', 10) || 0;

    const nextCartQty = currentCartQty + quantityToAdd;

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(nextCartQty);
    } else if (quantityInput) {
      quantityInput.setAttribute('data-cart-quantity', nextCartQty.toString());
    }

    this.#updateQuantityLabel(nextCartQty);
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    const bundleItems = this.#getBundleCartItems();
    const bundleSelection = this.#getCurrentBundleDiscount();
    if (bundleItems.length > 0 && (bundleSelection.quantity > 1 || bundleItems.length > 1)) {
      this.#processBatchAddToCart(bundleItems, event);
      return;
    }

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(bundleItems[0]?.variantId, bundleItems[0]?.quantity, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  #setupBundleSelector() {
    this.addEventListener('change', this.#onBundleChange, { signal: this.#abortController.signal });
    this.#syncBundleVariantControlsFromNativePicker();
    this.#syncBundleQuantity();
    this.#syncBundleVisuals();
    this.#syncBundlePrices();
  }

  /** @param {Event} event */
  #onBundleChange = (event) => {
    const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    if (!target?.closest('.ch-bundle-selector')) return;

    const changedUnit = target.closest('.ch-bundle-unit');

    if (target.matches('.ch-bundle-variant__select')) {
      this.#syncNativePickerFromBundleUnit(changedUnit || target.closest('.ch-bundle-option'));
    }

    this.#syncBundleQuantity();
    this.#syncBundleVisuals(changedUnit);
    this.#syncBundlePrices();
  };

  /** @param {Element | null} scope */
  #syncNativePickerFromBundleUnit(scope) {
    if (!scope) return;

    const sectionRoot = this.closest('.shopify-section, dialog, product-card');
    const selects = /** @type {HTMLSelectElement[]} */ (
      Array.from(scope.querySelectorAll('.ch-bundle-variant__select'))
    ).sort((a, b) => Number(a.dataset.optionPosition || '0') - Number(b.dataset.optionPosition || '0'));
    let controlToNotify = null;

    selects.forEach((select) => {
      const option = select.selectedOptions?.[0];
      const optionValueId = option?.dataset.optionValueId;
      if (!optionValueId) return;

      const nativeControl = /** @type {HTMLInputElement | HTMLOptionElement | null} */ (
        sectionRoot?.querySelector(
          `variant-picker input[data-option-value-id="${CSS.escape(optionValueId)}"], variant-picker option[data-option-value-id="${CSS.escape(optionValueId)}"]`
        ) || null
      );
      if (!nativeControl) return;

      if (nativeControl instanceof HTMLInputElement) {
        if (!nativeControl.checked) {
          nativeControl.checked = true;
          controlToNotify = nativeControl;
        }
        return;
      }

      if (nativeControl instanceof HTMLOptionElement) {
        const nativeSelect = nativeControl.closest('select');
        if (nativeSelect && nativeSelect.value !== nativeControl.value) {
          nativeSelect.value = nativeControl.value;
          controlToNotify = nativeSelect;
        }
      }
    });

    controlToNotify?.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #syncBundleVariantControlsFromNativePicker() {
    const bundleSelector = this.refs.bundleSelector;
    if (!bundleSelector) return;
    if (bundleSelector.querySelector('.ch-bundle-unit')) return;

    const sectionRoot = this.closest('.shopify-section, dialog, product-card');
    const nativeSelections = /** @type {Array<HTMLInputElement | HTMLOptionElement>} */ (
      Array.from(
        sectionRoot?.querySelectorAll('variant-picker fieldset input:checked, variant-picker select option[selected]') || []
      )
    );
    if (nativeSelections.length === 0) return;

    nativeSelections.forEach((nativeSelection) => {
      const optionValueId = nativeSelection.dataset.optionValueId;
      if (!optionValueId) return;

      bundleSelector
        .querySelectorAll(`.ch-bundle-variant__select option[data-option-value-id="${CSS.escape(optionValueId)}"]`)
        .forEach((option) => {
          const select = option.closest('select');
          if (select instanceof HTMLSelectElement) {
            select.value = /** @type {HTMLOptionElement} */ (option).value;
          }
        });
    });
  }

  #syncBundleQuantity() {
    const bundleSelector = this.refs.bundleSelector;
    if (!bundleSelector) return;

    const checkedInput = /** @type {HTMLInputElement | null} */ (bundleSelector.querySelector('input:checked'));
    const quantity = Number(checkedInput?.value || '1') || 1;

    bundleSelector.querySelectorAll('.ch-bundle-option').forEach((option) => {
      option.classList.toggle('is-selected', option.contains(checkedInput));
    });
    this.#syncBundleOptionExpansion();

    const quantityInput = /** @type {HTMLInputElement | null} */ (
      this.refs.quantitySelector?.querySelector?.('input[ref="quantityInput"]')
    );
    if (quantityInput) {
      quantityInput.value = quantity.toString();
      this.refs.quantitySelector?.updateButtonStates?.();
      this.refs.quantitySelector?.onQuantityChange?.();
      this.#syncBundlePrices();
      return;
    }

    const formQuantityInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
    if (formQuantityInput) {
      formQuantityInput.value = quantity.toString();
    }

    this.#syncBundleVisuals();
    this.#syncBundlePrices();
  }

  #syncBundleOptionExpansion() {
    const bundleSelector = this.refs.bundleSelector;
    if (!bundleSelector) return;

    bundleSelector.querySelectorAll('.ch-bundle-option').forEach((option) => {
      const isSelected = option.classList.contains('is-selected');
      const variants = /** @type {HTMLElement | null} */ (option.querySelector('.ch-bundle-option__variants'));
      if (!variants) return;

      option.setAttribute('aria-expanded', isSelected ? 'true' : 'false');
      variants.toggleAttribute('hidden', !isSelected);
      variants.style.display = isSelected ? 'grid' : 'none';
    });
  }

  /** @returns {Array<{id: number|string, available?: boolean, options?: string[], price?: number|string, mediaId?: number|string|null, image?: string|null}>} */
  #getBundleVariantData() {
    const bundleSelector = this.refs.bundleSelector;
    const dataScript = /** @type {HTMLScriptElement | null} */ (
      bundleSelector?.querySelector('.ch-bundle-variant-data')
    );
    if (!dataScript?.textContent) return [];

    try {
      const parsed = JSON.parse(dataScript.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }

  /** @param {Element | null} scope */
  #findBundleVariant(scope) {
    const variants = this.#getBundleVariantData();
    if (!scope || variants.length === 0) return null;

    const selects = /** @type {HTMLSelectElement[]} */ (
      Array.from(scope.querySelectorAll('.ch-bundle-variant__select'))
    ).sort((a, b) => Number(a.dataset.optionPosition || '0') - Number(b.dataset.optionPosition || '0'));

    if (selects.length === 0) {
      const currentId = this.refs.variantId?.value;
      return variants.find((variant) => variant.id?.toString() === currentId?.toString()) || variants[0] || null;
    }

    const matchingVariants = variants.filter((variant) => {
      if (!Array.isArray(variant.options)) return false;
      return selects.every((select) => {
        const optionIndex = Number(select.dataset.optionPosition || '1') - 1;
        return variant.options?.[optionIndex] === select.value;
      });
    });

    return matchingVariants.find((variant) => variant.available !== false) || matchingVariants[0] || null;
  }

  /** @param {number} cents */
  #formatBundleMoney(cents) {
    const bundleSelector = this.refs.bundleSelector;
    const moneyFormat = bundleSelector?.dataset.moneyFormat || '{{ amount }}';
    const amount = Math.max(0, Math.round(Number(cents) || 0));

    const formatWithDelimiters = (value, precision = 2, thousands = ',', decimal = '.') => {
      const normalized = (Math.max(0, value) / 100).toFixed(precision);
      const [dollars, centsPart] = normalized.split('.');
      const dollarsWithDelimiter = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return centsPart ? `${dollarsWithDelimiter}${decimal}${centsPart}` : dollarsWithDelimiter;
    };

    const placeholder = moneyFormat.match(
      /\{\{\s*(amount(?:_no_decimals)?(?:_with_comma_separator|_with_apostrophe_separator|_with_space_separator)?)\s*\}\}/
    )?.[1];

    const formattedAmount = (() => {
      switch (placeholder) {
        case 'amount_no_decimals':
          return formatWithDelimiters(amount, 0);
        case 'amount_with_comma_separator':
          return formatWithDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return formatWithDelimiters(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return formatWithDelimiters(amount, 2, "'", '.');
        case 'amount_no_decimals_with_apostrophe_separator':
          return formatWithDelimiters(amount, 0, "'", '.');
        case 'amount_with_space_separator':
          return formatWithDelimiters(amount, 2, ' ', '.');
        case 'amount_no_decimals_with_space_separator':
          return formatWithDelimiters(amount, 0, ' ', '.');
        case 'amount':
        default:
          return formatWithDelimiters(amount, 2);
      }
    })();

    if (placeholder) {
      return moneyFormat.replace(
        /\{\{\s*amount(?:_no_decimals)?(?:_with_comma_separator|_with_apostrophe_separator|_with_space_separator)?\s*\}\}/,
        formattedAmount
      );
    }

    const currency = bundleSelector?.dataset.currency || '';
    return `${formattedAmount}${currency ? ` ${currency}` : ''}`;
  }

  #syncBundlePrices() {
    const bundleSelector = this.refs.bundleSelector;
    if (!bundleSelector) return;

    const basePrice = Number(bundleSelector.dataset.basePrice || '0') || 0;

    bundleSelector.querySelectorAll('.ch-bundle-option').forEach((option) => {
      const quantity = Math.max(1, Number(option.dataset.quantity || '1') || 1);
      const discount = Math.max(0, Number(option.dataset.discount || '0') || 0);
      const rows = /** @type {Element[]} */ (Array.from(option.querySelectorAll('.ch-bundle-unit')));

      const compareTotal =
        rows.length > 0
          ? rows.reduce((sum, row) => sum + (Number(this.#findBundleVariant(row)?.price) || basePrice), 0)
          : (Number(this.#findBundleVariant(option)?.price) || basePrice) * quantity;
      const finalTotal = Math.round((compareTotal * (100 - discount)) / 100);

      const totalElement = option.querySelector('[data-bundle-total]');
      const compareElement = option.querySelector('[data-bundle-compare]');

      if (totalElement) {
        totalElement.textContent = this.#formatBundleMoney(finalTotal);
      }

      if (compareElement) {
        compareElement.textContent = this.#formatBundleMoney(compareTotal);
        compareElement.toggleAttribute('hidden', discount <= 0 || compareTotal <= finalTotal);
      }
    });
  }

  #getBundleCartItems() {
    const bundleSelector = this.refs.bundleSelector;
    if (!bundleSelector) return [];

    const checkedInput = /** @type {HTMLInputElement | null} */ (bundleSelector.querySelector('input:checked'));
    const selectedOption = checkedInput?.closest('.ch-bundle-option');
    if (!selectedOption) return [];

    const selectedBundleQuantity = Math.max(1, Number(selectedOption.dataset.quantity || checkedInput?.value || '1') || 1);
    const discount = Math.max(0, Number(selectedOption.dataset.discount || '0') || 0);
    const bundleKey = [
      'bundle',
      this.dataset.productId || '',
      Date.now().toString(36),
      Math.random().toString(36).slice(2, 8),
    ].filter(Boolean).join('-');
    const rows = /** @type {Element[]} */ (Array.from(selectedOption.querySelectorAll('.ch-bundle-unit')));
    const variantCounts = new Map();

    if (rows.length > 0) {
      rows.forEach((row) => {
        const variant = this.#findBundleVariant(row);
        if (!variant?.id) return;
        const id = variant.id.toString();
        variantCounts.set(id, (variantCounts.get(id) || 0) + 1);
      });
    } else {
      const variant = this.#findBundleVariant(selectedOption);
      if (variant?.id) {
        variantCounts.set(variant.id.toString(), selectedBundleQuantity);
      }
    }

    return Array.from(variantCounts, ([variantId, quantity]) => ({
      variantId,
      quantity,
      bundleQuantity: selectedBundleQuantity,
      bundleDiscount: discount,
      bundleKey,
    }));
  }

  /** @param {{mediaId?: number|string|null} | null} variant */
  #selectBundleVariantMedia(variant) {
    const mediaId = variant?.mediaId?.toString();
    if (!mediaId) return;

    const sectionRoot = this.closest('.shopify-section, dialog, product-card');
    const mediaGallery = /** @type {(HTMLElement & {slideshow?: any, preloadImage?: (index: number) => void}) | null} */ (
      sectionRoot?.querySelector('media-gallery') || null
    );
    const slideshow = mediaGallery?.slideshow;
    if (!slideshow || typeof slideshow.select !== 'function') return;

    const slides = /** @type {HTMLElement[]} */ (Array.from(slideshow.slides || []));
    const slideIndex = slides.findIndex((slide) => slide.getAttribute('slide-id') === mediaId);
    if (slideIndex === -1) return;

    slideshow.select({ id: mediaId }, undefined, { animate: false });
    mediaGallery?.preloadImage?.(slideIndex);
  }

  /** @param {Element | null} [focusScope] */
  #syncBundleVisuals(focusScope = null) {
    const bundleSelector = this.refs.bundleSelector;
    if (!bundleSelector) return;

    const fallbackImage = bundleSelector.dataset.bundleFallbackImage || '';
    const checkedInput = /** @type {HTMLInputElement | null} */ (bundleSelector.querySelector('input:checked'));
    const selectedOption = checkedInput?.closest('.ch-bundle-option');

    bundleSelector.querySelectorAll('.ch-bundle-option').forEach((option) => {
      const variant = this.#findBundleVariant(option);
      const image = variant?.image || fallbackImage;

      option.querySelectorAll('.ch-bundle-unit').forEach((unit) => {
        const unitVariant = this.#findBundleVariant(unit) || variant;
        const unitImage = unitVariant?.image || image;
        const thumb = unit.querySelector('[data-bundle-variant-thumb]');
        if (!(thumb instanceof HTMLImageElement) || !unitImage || thumb.src === unitImage) return;
        thumb.src = unitImage;
      });
    });

    const primaryScope = focusScope || selectedOption?.querySelector('.ch-bundle-unit') || selectedOption || null;
    const selectedVariant = this.#findBundleVariant(primaryScope);
    if (selectedVariant?.id && this.refs.variantId) {
      this.refs.variantId.value = selectedVariant.id.toString();
    }
    this.#selectBundleVariantMedia(selectedVariant);
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }
    const bundleDiscount = this.#getCurrentBundleDiscount();
    if (bundleDiscount.discount > 0 && bundleDiscount.quantity > 1) {
      formData.set('properties[_bundle_quantity]', String(bundleDiscount.quantity));
      formData.set('properties[_bundle_discount]', String(bundleDiscount.discount));
      formData.set('properties[_bundle_source]', 'product-page');
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    const pendingQuantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    document.dispatchEvent(
      new CustomEvent('ch:cart-add-start', {
        bubbles: true,
        detail: {
          source: 'product-form-component',
          productId: this.dataset.productId,
          variantId: formData.get('id')?.toString?.() || '',
          quantity: pendingQuantity,
          requestedItems: [{ variantId: formData.get('id')?.toString?.() || '', quantity: pendingQuantity }],
        },
      })
    );

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');
          const addedItemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          this.#optimisticallyIncrementCartQuantity(addedItemCount, id.toString());
          window.CHBundleDiscountSync?.syncSoon?.();

          document.dispatchEvent(
            new CustomEvent('ch:open-cart', {
              bubbles: true,
              detail: {
                source: 'product-form-component',
                productId: this.dataset.productId,
                variantId: id.toString(),
                quantity: addedItemCount,
                requestedItems: [{ variantId: id.toString(), quantity: addedItemCount }],
                addedItem: response,
              },
            })
          );

          this.dispatchEvent(
            new CartAddEvent(response, id.toString(), {
              source: 'product-form-component',
              itemCount: addedItemCount,
              productId: this.dataset.productId,
              variantId: id.toString(),
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * @param {Array<{variantId: string, quantity: number}>} items
   * @param {Event} [event]
   */
  #processBatchAddToCart(items, event) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
        properties: {
          _bundle_key: item.bundleKey || '',
          _bundle_quantity: String(item.bundleQuantity || items.reduce((sum, entry) => sum + entry.quantity, 0)),
          _bundle_discount: String(item.bundleDiscount || 0),
          _bundle_source: 'product-page',
        },
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    const pendingTotalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    document.dispatchEvent(
      new CustomEvent('ch:cart-add-start', {
        bubbles: true,
        detail: {
          source: 'product-form-component',
          productId: this.dataset.productId,
          quantity: pendingTotalQuantity,
          requestedItems: items.map((item) => ({ variantId: item.variantId, quantity: item.quantity })),
        },
      })
    );

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        if (items.length === 1) {
          this.#optimisticallyIncrementCartQuantity(items[0].quantity, items[0].variantId);
        }
        window.CHBundleDiscountSync?.syncSoon?.();

        document.dispatchEvent(
          new CustomEvent('ch:open-cart', {
            bubbles: true,
            detail: {
              source: 'product-form-component',
              productId: this.dataset.productId,
              quantity: totalQuantity,
              requestedItems: items.map((item) => ({ variantId: item.variantId, quantity: item.quantity })),
              addedItems: Array.isArray(response.items) ? response.items : [],
            },
          })
        );

        this.dispatchEvent(
          new CartAddEvent(response, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            variantId: items.length === 1 ? items[0].variantId : undefined,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  #getCurrentBundleDiscount() {
    const bundleSelector = this.refs.bundleSelector;
    if (!bundleSelector) return { quantity: 1, discount: 0 };

    const checkedInput = /** @type {HTMLInputElement | null} */ (bundleSelector.querySelector('input:checked'));
    const selectedOption = checkedInput?.closest('.ch-bundle-option');
    if (!selectedOption) return { quantity: 1, discount: 0 };

    const quantity = Math.max(1, Number(selectedOption.dataset.quantity || checkedInput?.value || '1') || 1);
    const discount = Math.max(0, Number(selectedOption.dataset.discount || '0') || 0);
    return { quantity, discount };
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    this.#syncBundleQuantity();
    this.#syncBundleVariantControlsFromNativePicker();
    this.#syncBundleVisuals();
    this.#syncBundlePrices();

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
